import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import { CONTENT_SECTIONS } from "../../config/portalAluno/contentSections";
import { getContentListing } from "../../services/portalAluno/contentListing/contentListingService";
import { getContentTags } from "../../services/portalAluno/tags/tagsService";
import { getSectionTopics } from "../../services/portalAluno/topics/topicsService";
import { portalContentListingQueryKey } from "./useContentListing";
import { portalTagsQueryKey } from "./useContentTags";
import { portalTopicsQueryKey } from "./useContentTopics";
import type { ContentSectionConfig } from "../../@types/portalAluno";
import type { ApplicationState } from "../../store";

const usePrefetchListingSections = (enabled = true) => {
  const queryClient = useQueryClient();
  const auth = useSelector((state: ApplicationState) => state.auth.data);
  const idUsuario = auth?.idUsuario;

  React.useEffect(() => {
    if (!enabled || !idUsuario) return;

    let isCancelled = false;

    const prefetchSection = async (section: ContentSectionConfig) => {
      await queryClient.prefetchInfiniteQuery({
        queryKey: portalContentListingQueryKey(idUsuario, section.apiType, null),
        queryFn: () => getContentListing(section, 0, null),
      });

      if (isCancelled) return;

      await queryClient.prefetchQuery({
        queryKey: portalTopicsQueryKey(idUsuario, section.topicType),
        queryFn: () => getSectionTopics(section),
      });

      if (isCancelled || !section.tagFiltered) return;

      await queryClient.prefetchQuery({
        queryKey: portalTagsQueryKey(idUsuario),
        queryFn: () => getContentTags(),
      });
    };

    const prefetchSections = async () => {
      for (const section of CONTENT_SECTIONS) {
        if (isCancelled) return;

        await prefetchSection(section);
      }
    };

    void prefetchSections();

    return () => {
      isCancelled = true;
    };
  }, [enabled, idUsuario, queryClient]);
};

export default usePrefetchListingSections;
